import React, { useEffect, useMemo, useState } from 'react'
import { getUpstreamEnrichmentFields, getUpstreamCollateralMaterialIds } from '../workflow-utils'
import { ENRICHMENT_FIELD_SHORT_LABELS } from '../enrichmentCatalog'
import { nodeLabel, nodeMeta, nodeTextClass, nodeTone } from '../nodeCatalog'
import { X, CaretDown, CaretRight, Database } from '@phosphor-icons/react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import type { Node } from 'reactflow'
import type { WorkflowNodeData, WorkflowNode, WorkflowEdge } from '@/types/workflow'
import SendEmailConfig from './config/SendEmailConfig'
import TriggerConfig from './config/TriggerConfig'
import DelayConfig from './config/DelayConfig'
import ConditionConfig from './config/ConditionConfig'
import ActionConfig from './config/ActionConfig'
import ABTestConfig from './config/ABTestConfig'
import GoalConfig from './config/GoalConfig'
import GenerateDeckConfig from './config/GenerateDeckConfig'
import EnrichContactsConfig from './config/EnrichContactsConfig'
import InternalAlertConfig from './config/InternalAlertConfig'
import ExitWorkflowConfig from './config/ExitWorkflowConfig'
import CreateTaskConfig from './config/CreateTaskConfig'
import HttpRequestConfig from './config/HttpRequestConfig'
import RateLimitConfig from './config/RateLimitConfig'
import BranchByFieldConfig from './config/BranchByFieldConfig'
import LinkedInActionConfig from './config/LinkedInActionConfig'
import SmsMessageConfig from './config/SmsMessageConfig'
import AssignOwnerConfig from './config/AssignOwnerConfig'
import AiDecisionConfig from './config/AiDecisionConfig'
import CrmSyncConfig from './config/CrmSyncConfig'
import ScheduleMeetingConfig from './config/ScheduleMeetingConfig'
import AutoReplyConfig from './config/AutoReplyConfig'
import { TestNodePanel } from './TestNodePanel'

interface NodeConfigPanelProps {
  node: Node<WorkflowNodeData>
  onClose: () => void
  onUpdate: (nodeId: string, data: Partial<WorkflowNodeData>) => void
  onDelete: (nodeId: string) => void
  nodes?: WorkflowNode[]
  edges?: WorkflowEdge[]
  workflowId?: number
}

// Compact chip labels for the "Available Enrichment Data" section. Sourced from
// the shared enrichment catalog so the chips, their count, and the fields the
// Send Email node offers stay in lockstep (see AB#21980).
const ENRICHMENT_FIELD_LABELS = ENRICHMENT_FIELD_SHORT_LABELS

const NodeConfigPanel: React.FC<NodeConfigPanelProps> = ({ node, onClose, onUpdate, onDelete, nodes, edges, workflowId }) => {
  const updateField = (field: string, value: any) => {
    onUpdate(node.id, { [field]: value })
  }

  const upstreamEnrichmentFields = useMemo(
    () => getUpstreamEnrichmentFields(node.id, nodes || [], edges || []),
    [node.id, nodes, edges],
  )

  const upstreamCollateralMaterialIds = useMemo(
    () => getUpstreamCollateralMaterialIds(node.id, nodes || [], edges || []),
    [node.id, nodes, edges],
  )

  // Assign Owner only stamps an owner onto tasks, so it is a no-op unless the
  // workflow has a Create Task node. Used to warn the user in AssignOwnerConfig.
  const hasCreateTaskNode = useMemo(
    () => (nodes || []).some((n) => n.type === 'createTask'),
    [nodes],
  )

  const IconComponent = nodeMeta(node.type).icon

  const [confirmDelete, setConfirmDelete] = useState(false)
  // The last AI preview generated for a sendEmail node's "Preview" tab, if
  // any — handed to TestNodePanel so "Run Test" sends this exact content
  // instead of asking the backend to run the LLM a second, independent time
  // (AB#21906: two separate generations of the same prompt aren't
  // guaranteed to produce the same text, so preview and actual send could
  // visibly differ even with identical context).
  const [previewEmail, setPreviewEmail] = useState<{ subject: string; body: string } | null>(null)
  // Selecting a different node re-uses this panel instance, so state left
  // over from the previous node would otherwise leak into the new one — a
  // confirm dialog resolving against the wrong node, or a stale preview
  // being sent as this node's test email.
  useEffect(() => {
    setConfirmDelete(false)
    setPreviewEmail(null)
  }, [node.id])

  const renderConfig = () => {
    const props = { data: node.data, updateField }
    switch (node.type) {
      case 'trigger': return <TriggerConfig {...props} />
      case 'sendEmail':
        return (
          <SendEmailConfig
            {...props}
            nodes={nodes}
            edges={edges}
            nodeId={node.id}
            workflowId={workflowId}
            upstreamEnrichmentFields={upstreamEnrichmentFields}
            upstreamCollateralMaterialIds={upstreamCollateralMaterialIds}
            onPreviewGenerated={setPreviewEmail}
          />
        )
      case 'delay': return <DelayConfig {...props} />
      case 'condition': return <ConditionConfig {...props} />
      case 'action': return <ActionConfig {...props} />
      case 'abTest': return <ABTestConfig {...props} />
      case 'goal': return <GoalConfig {...props} />
      case 'generateDeck':
        return (
          <GenerateDeckConfig
            {...props}
            upstreamEnrichmentFields={upstreamEnrichmentFields}
          />
        )
      case 'enrichContacts': return <EnrichContactsConfig {...props} />
      case 'internalAlert': return <InternalAlertConfig {...props} />
      case 'exitWorkflow': return <ExitWorkflowConfig {...props} />
      case 'createTask': return <CreateTaskConfig {...props} />
      case 'httpRequest': return <HttpRequestConfig {...props} />
      case 'rateLimit': return <RateLimitConfig {...props} />
      case 'branchByField': return <BranchByFieldConfig {...props} />
      case 'linkedInAction': return <LinkedInActionConfig {...props} />
      case 'smsMessage': return <SmsMessageConfig {...props} />
      case 'assignOwner': return <AssignOwnerConfig {...props} hasCreateTaskNode={hasCreateTaskNode} />
      case 'aiDecision': return <AiDecisionConfig {...props} />
      case 'crmSync': return <CrmSyncConfig {...props} />
      case 'scheduleMeeting': return <ScheduleMeetingConfig {...props} />
      case 'autoReply': return <AutoReplyConfig {...props} />
      default: return <p className="text-sm text-muted-foreground">No configuration available</p>
    }
  }

  return (
    <div className="w-96 bg-card dark:bg-[#0f1011] border-l border-border/60 dark:border-white/[0.06] h-full overflow-y-auto flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/50 dark:border-white/[0.06] flex-shrink-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`h-7 w-7 rounded-md border flex items-center justify-center flex-shrink-0 ${nodeTone(node.type).chip}`}>
            <IconComponent className={`w-3.5 h-3.5 ${nodeTextClass(node.type)}`} />
          </div>
          <h3
            className="text-foreground dark:text-[#f7f8f8] truncate"
            style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.16px' }}
          >
            {nodeLabel(node.type)} Settings
          </h3>
        </div>
        <button
          onClick={onClose}
          aria-label="Close panel"
          className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors duration-150"
        >
          <X className="w-3.5 h-3.5" weight="bold" />
        </button>
      </div>

      {/* Config Form */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {renderConfig()}

        {/* Available Enrichment Data */}
        {upstreamEnrichmentFields.length > 0 && node.type !== 'enrichContacts' && (
          <EnrichmentDataSection fields={upstreamEnrichmentFields} />
        )}

        {/* generateDeck in AI Generate mode has its own Run Test button inside the config
            (handleGeneratePreview), so suppress the duplicate TestNodePanel only there.
            Attach Direct mode still needs the TestNodePanel — it has no inline test button. */}
        {!(node.type === 'generateDeck' && (node.data.generate_deck_config?.mode ?? 'ai_generate') === 'ai_generate') && (
          <TestNodePanel
            nodeType={node.type as any}
            nodeData={node.data}
            pregeneratedEmail={node.type === 'sendEmail' ? previewEmail || undefined : undefined}
          />
        )}
      </div>

      {/* Footer — quiet destructive action, not a giant red bar */}
      <div className="px-5 py-3 border-t border-border/50 dark:border-white/[0.06] flex-shrink-0">
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-md border border-red-500/25 dark:border-red-500/30 bg-red-500/[0.06] dark:bg-red-500/[0.10] text-red-600 dark:text-red-400 hover:bg-red-500/[0.10] dark:hover:bg-red-500/[0.14] hover:border-red-500/40 dark:hover:border-red-500/45 transition-colors duration-150"
          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
        >
          Delete node
        </button>
      </div>

      {/* Deleting a node destroys its config and every edge touching it (§16). */}
      <ConfirmDialog
        open={confirmDelete}
        title="Delete node"
        description="Are you sure you want to delete this node? This action cannot be undone."
        confirmLabel="Delete"
        tone="destructive"
        onConfirm={() => {
          setConfirmDelete(false)
          onDelete(node.id)
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  )
}

const EnrichmentDataSection: React.FC<{ fields: string[] }> = ({ fields }) => {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="mt-4 border border-[#5e6ad2]/30 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-[#5e6ad2]/10 hover:bg-[#5e6ad2]/15 transition-colors duration-150"
      >
        <Database className="w-3.5 h-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
        <span className="text-xs font-medium text-[#5e6ad2] dark:text-[#a5b4fc] flex-1 text-left">
          Available Enrichment Data ({fields.length})
        </span>
        {expanded ? (
          <CaretDown className="w-3.5 h-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
        ) : (
          <CaretRight className="w-3.5 h-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
        )}
      </button>
      {expanded && (
        <div className="px-3 py-2 flex flex-wrap gap-1.5">
          {fields.map(field => (
            <span
              key={field}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#5e6ad2]/15 text-[#5e6ad2] dark:text-[#a5b4fc] border border-[#5e6ad2]/20"
            >
              {ENRICHMENT_FIELD_LABELS[field] || field.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default NodeConfigPanel
